import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { ApiErrorCodes } from '@/api/errors';
import { Snackbar, type SnackbarAction } from '@/components/Snackbar';
import { useToastSlot } from '@/components/toastStack';
import { SETTINGS_PATH } from '../settingsPath';
import {
  cancelSave,
  copyShareLink,
  dismissSave,
  getSaves,
  subscribeSaves,
  type SaveJob,
} from './saveStore';
import './save.css';

/**
 * The word on a *Save to Nextcloud* (ADR 0086): "Saving report.pdf to
 * Nextcloud… · Cancel" while it runs, then "Saved report.pdf to Mail
 * attachments/Received/2026-09 · Open in Nextcloud · Copy share link".
 *
 * Mounted once per window -- by the mail view and by the popped-out message
 * window -- and fed by the save store, so it outlives the reading pane that
 * started the save. The percentage is beside the sentence, hidden from
 * assistive technology: the live region says the sentence when it changes
 * (started, finished, failed), not every few hundred milliseconds.
 */

const NOTICE_MS = 10_000;

/** The job the snackbar speaks for: the newest one not dismissed. */
function currentJob(jobs: readonly SaveJob[]): SaveJob | undefined {
  for (let i = jobs.length - 1; i >= 0; i -= 1) {
    const job = jobs[i]!;
    if (!job.dismissed) return job;
  }
  return undefined;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The snackbar's sentence for a job. Exported for the tests. */
export function saveSentence(job: SaveJob): string {
  const files = job.files;
  const count = files.length;
  const saved = files.filter((f) => f.state === 'saved');
  const failed = files.filter((f) => f.state === 'failed');
  const only = count === 1 ? files[0] : undefined;
  const folder = job.folder === undefined || job.folder === '' ? 'Nextcloud' : job.folder;

  if (job.share !== undefined) {
    switch (job.share.state) {
      case 'working':
        return 'Making a share link…';
      case 'copied':
        return job.share.password !== undefined
          ? `Link copied. Its password is ${job.share.password} — send that separately.`
          : 'Share link copied.';
      case 'shown':
        return `Could not copy. The link is ${job.share.url ?? ''}${
          job.share.password !== undefined ? ` (password ${job.share.password})` : ''
        }`;
      case 'failed':
        return `No share link: ${job.share.problem ?? 'something went wrong.'}`;
      default:
    }
  }
  switch (job.state) {
    case 'queued':
      return only ? `Waiting to save ${only.filename} to Nextcloud…` : `Waiting to save ${plural(count, 'file')} to Nextcloud…`;
    case 'running':
      return only ? `Saving ${only.filename} to Nextcloud…` : `Saving ${plural(count, 'file')} to Nextcloud…`;
    case 'cancelled':
      return saved.length > 0
        ? `Stopped. ${saved.length} of ${count} saved to ${folder}.`
        : 'Save to Nextcloud stopped.';
    case 'failed':
      return saved.length > 0
        ? `${saved.length} of ${count} saved to ${folder}. ${job.problem ?? ''}`.trim()
        : (job.problem ?? 'Nothing could be saved to Nextcloud.');
    case 'done':
    default:
  }
  if (failed.length === 0 && only?.name !== undefined) {
    return only.renamed === true
      ? `Saved as ${only.name} in ${folder} (${only.filename} was already there).`
      : `Saved ${only.name} to ${folder}.`;
  }
  if (failed.length === 0) return `Saved ${plural(count, 'file')} to ${folder}.`;
  const first = failed[0]?.problem ?? 'It could not be saved.';
  if (saved.length === 0) return count === 1 ? first : `None of the ${count} files were saved. ${first}`;
  return `Saved ${saved.length} of ${count} to ${folder}. ${first}`;
}

/** The share link can only be made for one file, saved. */
function sharable(job: SaveJob): boolean {
  return job.state === 'done' && job.files.length === 1 && job.files[0]?.state === 'saved';
}

export function SaveSnackbar() {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const slot = useToastSlot();
  const jobs = useSyncExternalStore(subscribeSaves, getSaves, getSaves);
  const job = currentJob(jobs);
  const [held, setHeld] = useState(false);

  const busy = job !== undefined && (job.state === 'running' || job.state === 'queued' || job.share?.state === 'working');
  // Dismissed on its own a while after it has said how it went; never while
  // it is running or held (hover, focus: WCAG 2.2.1).
  useEffect(() => {
    if (job === undefined || busy || held) return;
    const timer = setTimeout(() => dismissSave(job.id), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [job, busy, held]);

  let message: string | undefined;
  let detail: ReactNode;
  let actionLabel: string | undefined;
  let onAction: (() => void) | undefined;
  const more: SnackbarAction[] = [];
  if (job !== undefined) {
    message = saveSentence(job);
    if (job.state === 'running' || job.state === 'queued') {
      const total = job.files.reduce((sum, f) => sum + Math.max(f.totalBytes, 0), 0);
      const done = job.files.reduce((sum, f) => sum + (f.state === 'saved' ? f.totalBytes : f.doneBytes), 0);
      const percent = total > 0 ? Math.min(100, Math.floor((done / total) * 100)) : 0;
      detail = (
        <span className="nc-save-percent" aria-hidden="true">
          {percent}%
        </span>
      );
      actionLabel = 'Cancel';
      onAction = () => cancelSave(job.id);
    } else if (job.problemCode === ApiErrorCodes.NextcloudRejected || job.problemCode === ApiErrorCodes.NextcloudNotLinked) {
      actionLabel = 'Open Settings';
      onAction = () => {
        dismissSave(job.id);
        void navigate(SETTINGS_PATH);
      };
    } else {
      const saved = job.files.filter((f) => f.state === 'saved');
      const only = job.files.length === 1 ? saved[0] : undefined;
      if (only?.openUrl !== undefined) {
        more.push({ label: 'Open in Nextcloud', href: only.openUrl });
      } else if (saved.length > 0 && job.folderUrl !== undefined) {
        more.push({ label: 'Open folder', href: job.folderUrl });
      }
      if (sharable(job) && job.share?.state !== 'copied' && job.share?.state !== 'shown') {
        more.push({
          label: job.share?.state === 'working' ? 'Making a link…' : 'Copy share link',
          disabled: job.share?.state === 'working',
          onAction: () => void copyShareLink({ api, queryClient }, job.id),
        });
      }
    }
  }

  const snackbar = (
    <Snackbar
      className="snackbar--nc-save"
      message={message}
      {...(job !== undefined ? { messageKey: `${job.id}:${job.state}:${job.share?.state ?? ''}` } : {})}
      {...(actionLabel !== undefined && onAction !== undefined ? { actionLabel, onAction } : {})}
      moreActions={more}
      detail={detail}
      onHoldChange={setHeld}
      {...(job !== undefined && !busy ? { onDismiss: () => dismissSave(job.id) } : {})}
    />
  );
  return slot ? createPortal(snackbar, slot) : snackbar;
}
